// frontend/src/pages/Admin/AdminUserManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import styles from './AdminUserManagement.module.css';
// Import user service functions
import {
    getPendingUsers,
    getAllUsers,
    approveUser,
    rejectUser,
    assignAccountToUser,
    createUser,
    deleteUser,
    updateUserRole
} from '../../services/userService';
// Import account service functions
import { listAllAccounts, createAccountByAdmin, updateAccountByAdmin } from '../../services/accountService';
import Button from '../../components/Button/Button';
import FormInput from '../../components/FormInput/FormInput';
import { useAuth } from '../../contexts/AuthContext';

// --- Define a Modal component ---
const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h3>{title}</h3>
                    <button onClick={onClose} className={styles.closeButton}>&times;</button>
                </div>
                <div className={styles.modalBody}>
                    {children}
                </div>
            </div>
        </div>
    );
};

const AdminUserManagement = () => {
    const { currentUser } = useAuth();
    // State management
    const [activeTab, setActiveTab] = useState('all'); // 'all' | 'pending'
    const [allUsers, setAllUsers] = useState([]);
    const [pendingUsers, setPendingUsers] = useState([]);
    const [accountsList, setAccountsList] = useState([]);

    const [loading, setLoading] = useState(false);
    const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
    const [error, setError] = useState(null);

    // Action loading states
    const [actionLoading, setActionLoading] = useState({ id: null, type: null });

    // Modal states
    const [showAssignAccountModal, setShowAssignAccountModal] = useState(false);
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);

    // Assign modal state
    const [targetUserForAccountAction, setTargetUserForAccountAction] = useState(null);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [newAccountName, setNewAccountName] = useState('');
    const [assignActionLoading, setAssignActionLoading] = useState(false);
    const [assignActionError, setAssignActionError] = useState(null);
    const [showInDropdown, setShowInDropdown] = useState(true);

    // Create user modal state
    const [newUserForm, setNewUserForm] = useState({
        username: '',
        password: '',
        role: 'Member',
        accountId: ''
    });
    const [createActionLoading, setCreateActionLoading] = useState(false);
    const [createActionError, setCreateActionError] = useState(null);

    // Fetching data
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [pending, all] = await Promise.all([
                getPendingUsers(),
                getAllUsers()
            ]);
            setPendingUsers(pending);
            setAllUsers(all);
        } catch (err) {
            setError(err.message || 'Could not fetch user data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();

        const fetchAccounts = async () => {
            setIsLoadingAccounts(true);
            try {
                const accounts = await listAllAccounts();
                setAccountsList(accounts);
            } catch (err) {
                console.error("Error fetching accounts:", err);
            } finally {
                setIsLoadingAccounts(false);
            }
        };
        fetchAccounts();
    }, [fetchData]);

    // Handlers
    const handleOpenAssignModal = (user) => {
        setTargetUserForAccountAction(user);
        setSelectedAccountId(user.accountId || '');
        setNewAccountName('');
        setAssignActionError(null);
        const currentAccount = accountsList.find(a => a.id === user.accountId);
        setShowInDropdown(currentAccount ? currentAccount.showInDropdown : true);
        setShowAssignAccountModal(true);
    };

    const handleConfirmApprovalAndAssignment = async () => {
        setAssignActionLoading(true);
        setAssignActionError(null);
        try {
            let finalAccountId = selectedAccountId;
            if (finalAccountId) {
                await assignAccountToUser(targetUserForAccountAction.id, finalAccountId);
                // Also update showInDropdown for this account if it was an existing one selected or just created
                // Wait, if it was CREATE_NEW_ACCOUNT, the createAccountByAdmin already took it if we pass it.
                // If it was an existing account, we might want to update it? 
                // Let's keep it simple: if existing, we update it.
                if (selectedAccountId !== 'CREATE_NEW_ACCOUNT') {
                    await updateAccountByAdmin({ id: finalAccountId, showInDropdown });
                }
            }

            await fetchData();
            const accounts = await listAllAccounts();
            setAccountsList(accounts);
            setShowAssignAccountModal(false);
        } catch (err) {
            setAssignActionError(err.message || 'Action failed.');
        } finally {
            setAssignActionLoading(false);
        }
    };

    const handleCreateAccount = async (e) => {
        e.preventDefault();
        setAssignActionLoading(true);
        setAssignActionError(null);
        try {
            if (!newAccountName.trim()) throw new Error('Account name is required.');
            await createAccountByAdmin({
                accountName: newAccountName,
                showInDropdown: showInDropdown
            });
            const accounts = await listAllAccounts();
            setAccountsList(accounts);
            setShowCreateAccountModal(false);
            setNewAccountName('');
        } catch (err) {
            setAssignActionError(err.message || 'Creation failed.');
        } finally {
            setAssignActionLoading(false);
        }
    };

    const handleReject = async (userId) => {
        if (!window.confirm("Are you sure you want to reject this registration?")) return;
        setActionLoading({ id: userId, type: 'reject' });
        try {
            await rejectUser(userId);
            await fetchData();
        } catch (err) {
            setError(`Rejection failed: ${err.message}`);
        } finally {
            setActionLoading({ id: null, type: null });
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm("Are you sure you want to DELETE this user? This cannot be undone.")) return;
        setActionLoading({ id: userId, type: 'delete' });
        try {
            await deleteUser(userId);
            await fetchData();
        } catch (err) {
            setError(`Deletion failed: ${err.message}`);
        } finally {
            setActionLoading({ id: null, type: null });
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setCreateActionLoading(true);
        setCreateActionError(null);
        try {
            await createUser(newUserForm);
            await fetchData();
            setShowCreateUserModal(false);
            setNewUserForm({ username: '', password: '', role: 'Member', accountId: '' });
        } catch (err) {
            setCreateActionError(err.message || 'Creation failed.');
        } finally {
            setCreateActionLoading(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        setActionLoading({ id: userId, type: 'role-change' });
        try {
            await updateUserRole(userId, newRole);
            await fetchData();
        } catch (err) {
            setError(`Failed to update user role: ${err.message}`);
        } finally {
            setActionLoading({ id: null, type: null });
        }
    };

    return (
        <div className={styles.adminUserManagementContainer}>
            <div className={styles.managementHeader}>
                <h2 className="page-main-title">User Management</h2>
                <div className={styles.headerActions}>
                    <Button
                        onClick={() => setShowCreateAccountModal(true)}
                        variant="secondary"
                        className={styles.createAccountBtn}
                    >
                        + Create Account
                    </Button>
                    <Button
                        onClick={() => setShowCreateUserModal(true)}
                        variant="primary"
                        className={styles.createUserBtn}
                    >
                        + Create User
                    </Button>
                </div>
            </div>

            <div className={styles.tabs}>
                <div
                    className={`${styles.tab} ${activeTab === 'all' ? styles.active : ''}`}
                    onClick={() => setActiveTab('all')}
                >
                    All Users ({allUsers.length})
                </div>
                <div
                    className={`${styles.tab} ${activeTab === 'pending' ? styles.active : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    Pending Approval ({pendingUsers.length})
                </div>
            </div>

            {error && <p className={styles.errorMessage}>{error}</p>}

            {loading ? (
                <p>Loading users...</p>
            ) : (
                <div className={styles.tableContainer}>
                    <table className={styles.userTable}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Username</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(activeTab === 'all' ? allUsers : pendingUsers).map(user => (
                                <tr key={user.id}>
                                    <td>{user.id}</td>
                                    <td>{user.username || user.email}</td>
                                    <td>
                                        <select
                                            className={`${styles.roleSelect} ${user.role === 'Administrator' ? styles.roleAdmin : styles.roleMember}`}
                                            value={user.role}
                                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                            disabled={actionLoading.id === user.id || (currentUser && currentUser.id === user.id)}
                                        >
                                            <option value="Member">Member</option>
                                            <option value="Administrator">Administrator</option>
                                        </select>
                                    </td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${user.status === 'active' ? styles.statusActive : styles.statusPending}`}>
                                            {user.status}
                                        </span>
                                    </td>
                                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                                    <td>
                                        <div className={styles.actionButtons}>
                                            {user.status === 'pending_approval' ? (
                                                <>
                                                    <Button
                                                        onClick={() => handleOpenAssignModal(user)}
                                                        variant="primary" size="sm"
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleReject(user.id)}
                                                        variant="outlineDanger" size="sm"
                                                        disabled={actionLoading.id === user.id}
                                                    >
                                                        Reject
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        onClick={() => handleOpenAssignModal(user)}
                                                        variant="secondary" size="sm"
                                                    >
                                                        Edit Account
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleDelete(user.id)}
                                                        variant="outlineDanger" size="sm"
                                                        disabled={actionLoading.id === user.id}
                                                    >
                                                        Delete
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create User Modal */}
            <Modal
                isOpen={showCreateUserModal}
                onClose={() => setShowCreateUserModal(false)}
                title="Create New User"
            >
                <form onSubmit={handleCreateUser}>
                    {createActionError && <p className={styles.errorMessageModal}>{createActionError}</p>}
                    <div className={styles.formGroupModal}>
                        <FormInput
                            label="Username"
                            type="text"
                            value={newUserForm.username}
                            onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })}
                            required
                        />
                    </div>
                    <div className={styles.formGroupModal}>
                        <FormInput
                            label="Password"
                            type="password"
                            value={newUserForm.password}
                            onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                            required
                        />
                    </div>
                    <div className={styles.formGroupModal}>
                        <label>Role</label>
                        <select
                            value={newUserForm.role}
                            onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                        >
                            <option value="Member">Member</option>
                            <option value="Administrator">Administrator</option>
                        </select>
                    </div>
                    <div className={styles.formGroupModal}>
                        <label>Account</label>
                        <select
                            value={newUserForm.accountId}
                            onChange={(e) => setNewUserForm({ ...newUserForm, accountId: e.target.value })}
                        >
                            <option value="">-- No Account --</option>
                            {accountsList.map(acc => (
                                <option key={acc.id} value={acc.id}>{acc.accountName}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.modalActions}>
                        <Button type="button" variant="secondary" onClick={() => setShowCreateUserModal(false)}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={createActionLoading}>
                            {createActionLoading ? 'Creating...' : 'Create User'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Assign/Edit Account Modal */}
            <Modal
                isOpen={showAssignAccountModal}
                onClose={() => setShowAssignAccountModal(false)}
                title={`Account for ${targetUserForAccountAction?.username || targetUserForAccountAction?.email}`}
            >
                {assignActionError && <p className={styles.errorMessageModal}>{assignActionError}</p>}
                <div className={styles.formGroupModal}>
                    <label>Select Account</label>
                    <select
                        value={selectedAccountId}
                        onChange={(e) => setSelectedAccountId(e.target.value)}
                        disabled={assignActionLoading || isLoadingAccounts}
                    >
                        <option value="">-- No Account --</option>
                        {accountsList.map(acc => (
                            <option key={acc.id} value={acc.id}>{acc.accountName}</option>
                        ))}
                        <option value="CREATE_NEW_ACCOUNT">+ Create New Account</option>
                    </select>
                </div>

                {selectedAccountId === 'CREATE_NEW_ACCOUNT' && (
                    <div className={styles.formGroupModal}>
                        <FormInput
                            label="New Account Name"
                            type="text"
                            value={newAccountName}
                            onChange={(e) => setNewAccountName(e.target.value)}
                            required
                        />
                    </div>
                )}

                <div className={styles.formGroupModal}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={showInDropdown}
                            onChange={(e) => setShowInDropdown(e.target.checked)}
                            disabled={assignActionLoading}
                        />
                        Show in Reservation Dropdown
                    </label>
                </div>

                <div className={styles.modalActions}>
                    <Button variant="secondary" onClick={() => setShowAssignAccountModal(false)}>Cancel</Button>
                    <Button
                        variant="primary"
                        onClick={handleConfirmApprovalAndAssignment}
                        disabled={assignActionLoading}
                    >
                        {assignActionLoading ? 'Saving...' : 'Confirm'}
                    </Button>
                </div>
            </Modal>

            {/* Create Account Modal */}
            <Modal
                isOpen={showCreateAccountModal}
                onClose={() => setShowCreateAccountModal(false)}
                title="Create New Account"
            >
                <form onSubmit={handleCreateAccount}>
                    {assignActionError && <p className={styles.errorMessageModal}>{assignActionError}</p>}
                    <div className={styles.formGroupModal}>
                        <FormInput
                            label="Account Name"
                            type="text"
                            value={newAccountName}
                            onChange={(e) => setNewAccountName(e.target.value)}
                            required
                        />
                    </div>
                    <div className={styles.formGroupModal}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={showInDropdown}
                                onChange={(e) => setShowInDropdown(e.target.checked)}
                            />
                            Show in Reservation Dropdown
                        </label>
                    </div>
                    <div className={styles.modalActions}>
                        <Button type="button" variant="secondary" onClick={() => setShowCreateAccountModal(false)}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={assignActionLoading}>
                            {assignActionLoading ? 'Creating...' : 'Create Account'}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default AdminUserManagement;